/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {of} from 'rxjs';
import {FcAgUtilsService} from '../fc-ag-services/fc-ag-utils.service';
import {FcResponsiveAgGridService} from './fc-responsive-ag-grid.service';
import {
    FcResponsiveAgGridColumn,
    FcResponsiveAgGridOptions
} from './fc-responsive-ag-grid-options';

/**
 * Grid component with Angular.
 */
@Component({
    selector: 'fc-responsive-ag-grid',
    templateUrl: './fc-responsive-ag-grid.component.html',
    styleUrls: ['./fc-responsive-ag-grid.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FcResponsiveAgGridComponent implements OnInit {

    /**
     * Fc Responsive Grid Options.
     */
    @Input() gridOptions: FcResponsiveAgGridOptions;


    /**
     * Default constructor.
     *
     * @param fcAgUtilsService FcAgUtilsService.
     * @param fcResponsiveAgGridService FcResponsiveAgGridService.
     */
    constructor(
        private fcAgUtilsService: FcAgUtilsService,
        private fcResponsiveAgGridService: FcResponsiveAgGridService
    ) {}

    /**
     * Angular life cycle.
     */
    ngOnInit() {
        const defaultFcResponsiveAgGridOptions: FcResponsiveAgGridOptions = {
            isExpand: false,
            compareKey: '',
            isKeepSelectedList: false,
            mobileWidth: 1200,
            isMobileMode: false,
            data: [],
            allData: [],
            selectedList: [],
            globalSelectedList: [],
            dataLength: 0,
            columnDefs: [],
            enableSort: false,
            enablePagination: true,
            serverSort: true,
            serverPagination: true,
            multipleSelect: false,
            isCheckAllDisabled: false,
            isCheckAllHidden: false,
            pageSizeList: [],
            gridState: {
                orderBy: '',
                order: 'ASC',
                pageSize: 12,
                currentPage: 1
            },
            getGridData: (context?: any) => of({data: [], dataLength: 0}),
            cellClick: (event: any, item: any, column: FcResponsiveAgGridColumn,
                options: FcResponsiveAgGridOptions) => {
                if (column.cellClick) {
                    column.cellClick(event, item, column, options);
                }
            },
            renderCell: (item: any, column: FcResponsiveAgGridColumn, options: FcResponsiveAgGridOptions) => {
                if (column.renderCell) {
                    return column.renderCell(item, column, options);
                }
                return item && column.field && item[column.field];
            },
            rowClick: (event: any, item: any, options: FcResponsiveAgGridOptions) => {
                if (item.disabled || item._hideCheckbox) {
                    return;
                }
                item.expanded = !item.expanded;
                if (this.gridOptions.multipleSelect) {
                    item.selected = !item.selected;
                    this.gridOptions.selectedList = (this.gridOptions.data || []).filter(item => item.selected);
                    // Emit selected list in case server paging 
                    if (this.gridOptions.serverPagination) {
                        if (this.gridOptions.isKeepSelectedList && this.gridOptions.compareKey) {
                            this.updateGlobalSelectedList(item, this.gridOptions.compareKey);
                            this.fcResponsiveAgGridService.selectionChanged.next(
                                this.gridOptions.globalSelectedList || []);
                        } else {
                            this.fcResponsiveAgGridService.selectionChanged.next(this.gridOptions.selectedList);
                        }
                    } else {
                        // Emit selected list in case client paging 
                        const allSelectedList = (this.gridOptions.allData || []).filter(item => item.selected);
                        this.fcResponsiveAgGridService.selectionChanged.next(allSelectedList);
                    }
                } else {
                    (this.gridOptions.data || []).forEach(item => item.selected = false);
                    item.selected = true;
                    this.gridOptions.selectedList = [item];
                    this.fcResponsiveAgGridService.selectionChanged.next(this.gridOptions.selectedList);
                }
            },
            sortChange: (column: FcResponsiveAgGridColumn) => {
                if (this.gridOptions.enableSort && !column.disableSort) {
                    if (this.gridOptions.gridState) {
                        this.gridOptions.gridState.orderBy = column.field;
                        this.gridOptions.gridState.order = this.gridOptions.gridState.order === 'ASC' ? 'DESC' : 'ASC';
                    }
                    if (this.gridOptions.serverSort) {
                        if (this.gridOptions.setGridData) {
                            this.gridOptions.setGridData();
                        }
                    } else {
                        if (this.gridOptions.sortDataUI) {
                            this.gridOptions.sortDataUI();
                        }
                    }
                }
            },
            sortDataUI: () => {
                const active = this.gridOptions.gridState && this.gridOptions.gridState.orderBy;
                const direction = this.gridOptions.gridState && this.gridOptions.gridState.order;
                if (!active || direction == '') {
                    return;
                }
                const allDataSorted = this.gridOptions.allData && this.gridOptions.allData.sort((a, b) => {
                    let valueA = a[active];
                    let valueB = b[active];

                    const valueAType = typeof valueA;
                    const valueBType = typeof valueB;

                    if (valueAType !== valueBType) {
                        if (valueAType === 'number') {
                            valueA += '';
                        }
                        if (valueBType === 'number') {
                            valueB += '';
                        }
                    }

                    let comparatorResult = 0;
                    if (valueA != null && valueB != null) {
                        if (valueA > valueB) {
                            comparatorResult = 1;
                        } else if (valueA < valueB) {
                            comparatorResult = -1;
                        }
                    } else if (valueA != null) {
                        comparatorResult = 1;
                    } else if (valueB != null) {
                        comparatorResult = -1;
                    }
                    return comparatorResult * (direction == 'ASC' ? 1 : -1);

                }) || [];

                if (this.gridOptions.enablePagination) {
                    this.gridOptions.data = this.fcResponsiveAgGridService
                        .pagingUISide(allDataSorted, this.gridOptions).data;
                } else {
                    this.gridOptions.data = allDataSorted;
                }
                if (this.gridOptions.selectedList) {
                    this.gridOptions.selectedList = (this.gridOptions.data || []).filter(item => item.selected);
                }
            },
            isAllSelected: () => {
                if (this.gridOptions.isCheckAllDisabled) return false;
                const allActiveData = (this.gridOptions.data || [])
                    .filter(item => !item.disabled && !item._hideCheckbox);
                return (allActiveData || []).length > 0 &&
                    (this.gridOptions.selectedList || []).length === (allActiveData || []).length;
            },

            toggleAll: () => {
                if (this.gridOptions.isAllSelected && this.gridOptions.isAllSelected()) {
                    (this.gridOptions.data || []).forEach(item => {
                        item.selected = false;
                        if (this.gridOptions.isKeepSelectedList && this.gridOptions.compareKey) {
                            this.updateGlobalSelectedList(item, this.gridOptions.compareKey);
                        }
                    });
                    this.gridOptions.selectedList = [];
                } else {
                    let allActiveData: any = [];
                    (this.gridOptions.data || []).forEach(item => {
                        if (!item.disabled && !item._hideCheckbox) {
                            item.selected = true;
                            allActiveData.push(item);
                            if (this.gridOptions.isKeepSelectedList && this.gridOptions.compareKey) {
                                this.updateGlobalSelectedList(item, this.gridOptions.compareKey);
                            }
                        }
                    });
                    this.gridOptions.selectedList = allActiveData;
                }
            },
            setGridData: (context?: any) => {
                this.gridOptions.getGridData(context).subscribe((response) => {
                    if (this.gridOptions.serverPagination) {
                        this.gridOptions.selectedList = [];
                        this.gridOptions.data = response.data;
                        this.gridOptions.dataLength = response.dataLength;
                        if (this.gridOptions.isKeepSelectedList && this.gridOptions.compareKey) {
                            this.restoreSelectedListFromGlobalList(this.gridOptions.compareKey);
                        }
                    }  else {
                        // UI paging.
                        this.gridOptions.allData = response.data;
                        this.gridOptions.data = this.fcResponsiveAgGridService
                            .pagingUISide(this.gridOptions.allData, this.gridOptions).data;
                        this.gridOptions.dataLength = this.fcResponsiveAgGridService
                            .pagingUISide(this.gridOptions.allData, this.gridOptions).dataLength;
                    }
    
                });
            }
        };

        this.gridOptions.columnDefs = this.gridOptions.columnDefs.filter(item => !(item.isHidden && item.isHidden()));
        this.fcAgUtilsService.extendConfig(this.gridOptions, defaultFcResponsiveAgGridOptions);
        if (this.gridOptions.setGridData) {
            this.gridOptions.setGridData();
        }
    }

    /**
     * Update previous selected list.
     *
     * @param item Item.
     * @param compareKey CompareKey.
     */
    updateGlobalSelectedList(item: any, compareKey: string) {
        if (item.selected) {
            const exists = (this.gridOptions.globalSelectedList || [])
                .find(selectedItem => selectedItem[compareKey] === item[compareKey]);
            if (!exists) {
                this.gridOptions.globalSelectedList && this.gridOptions.globalSelectedList.push(item);
            }
        } else {
            this.gridOptions.globalSelectedList = (this.gridOptions.globalSelectedList || [])
                .filter(selectedItem => selectedItem[compareKey] !== item[compareKey]);
        }
    }

    /**
     * Update previous selected list.
     *
     * @param compareKey CompareKey.
     */
    restoreSelectedListFromGlobalList(compareKey: string) {
        (this.gridOptions.data || []).forEach(item => {
            const exists = (this.gridOptions.globalSelectedList || [])
                .find(selectedItem => selectedItem[compareKey] === item[compareKey]);
            if (exists) {
                item.selected = true;
                this.gridOptions.selectedList && this.gridOptions.selectedList.push(item);
            }
        });
    }
}